import {
  AccountTree,
  AssignmentOutlined,
  AutorenewOutlined,
  CorporateFare,
  ErrorOutline,
  FolderOpen,
  HourglassEmpty,
  InsertChartOutlined,
  MonetizationOnOutlined,
  NotificationsOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  FindInPageOutlined,
  ScheduleOutlined,
  ShoppingCartOutlined,
} from '@mui/icons-material'
import { Roles } from '@omnea-digital/toolbox'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { hrefFor, type NavigableRouteKey } from '@omnea-app/fe-authz/routeHref'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { isRouteActive } from '@omnea-app/fe-routing/routes/isRouteActive'
import { useGetMyTaskCount } from '@omnea-app/fe-tasks/useTasks'
import { StatsContext } from '@omnea-app/frontend/providers/StatsProvider'
import { SidebarUncontrolled, type MenuItemType } from './SidebarUncontrolled'

type NavItemCount = 'assessments' | 'renewals'

type RouteNavItem = {
  route: NavigableRouteKey
  labelKey: string
  Icon: React.ElementType
  /**
   * The sections this item highlights for, where they are not simply the path it links to — a legacy
   * address it still owns.
   *
   * TODO(PLT-1727): replace with route keys once those addresses are keyed.
   */
  activePaths?: readonly string[]
  count?: NavItemCount
  badgeKey?: string
}

type GroupNavItem = {
  labelKey: string
  Icon: React.ElementType
  children: readonly RouteNavItem[]
}

type NavItem = RouteNavItem | GroupNavItem

const isGroup = (item: NavItem): item is GroupNavItem => 'children' in item

const NAV_ITEMS: readonly NavItem[] = [
  { route: 'requestsList', labelKey: 'sidebar.resources-requests', Icon: HourglassEmpty },
  { route: 'suppliersList', labelKey: 'sidebar.resources-suppliers', Icon: CorporateFare },
  { route: 'assessmentsList', labelKey: 'sidebar.resources-assessments', Icon: AssignmentOutlined, count: 'assessments' },
  { route: 'agreementsList', labelKey: 'sidebar.resources-agreements', Icon: RequestQuoteOutlined },
  {
    route: 'purchaseOrdersList',
    labelKey: 'sidebar.resources-purchase-orders',
    Icon: ShoppingCartOutlined,
    badgeKey: 'sidebar.new-badge',
  },
  { route: 'renewalsList', labelKey: 'sidebar.resources-renewals', Icon: AutorenewOutlined, count: 'renewals' },
  { route: 'scheduledRequestsList', labelKey: 'sidebar.resources-scheduled-requests', Icon: ScheduleOutlined },
  {
    route: 'insights',
    labelKey: 'sidebar.resources-insights',
    Icon: InsertChartOutlined,
    // `/reporting` is the same product under its former address.
    activePaths: ['/insights', '/reporting'],
  },
  {
    labelKey: 'sidebar.resources-risk-management',
    Icon: ErrorOutline,
    children: [
      { route: 'risksList', labelKey: 'sidebar.resources-risks', Icon: ErrorOutline },
      { route: 'riskAlertsList', labelKey: 'sidebar.resources-alerts', Icon: NotificationsOutlined },
      { route: 'subprocessorsList', labelKey: 'sidebar.resources-subprocessors', Icon: AccountTree },
    ],
  },
  { route: 'documentsList', labelKey: 'sidebar.resources-documents', Icon: FolderOpen },
  { route: 'budgetsList', labelKey: 'sidebar.resources-budgets', Icon: MonetizationOnOutlined },
  { route: 'billsList', labelKey: 'sidebar.bills', Icon: ReceiptLongOutlined },
  { route: 'productCatalogues', labelKey: 'sidebar.resources-product-catalogues', Icon: ShoppingCartOutlined },
  { route: 'savingsDashboard', labelKey: 'sidebar.resources-savings-dashboard', Icon: FindInPageOutlined },
]

type Props = {
  path: string | null
  sidebarCollapsedByDefault?: boolean
}

export function Sidebar({ path, sidebarCollapsedByDefault }: Props) {
  const { t } = useTranslation('common')
  const { asPath } = useRouter()
  const { role } = useAuthenticatedUser().user
  const isExpandedExternal = sidebarCollapsedByDefault !== true

  const routeAccess = useRouteAccess()
  const { numAssessmentsActionNeeded, numRenewalsActionNeeded } = useContext(StatsContext)
  const { data: taskCount = 0 } = useGetMyTaskCount()

  const [isExpanded, toggleExpanded] = useState(isExpandedExternal)

  useEffect(() => {
    toggleExpanded(isExpandedExternal)
  }, [isExpandedExternal])

  const countsByName: Readonly<Record<NavItemCount, number | null>> = {
    assessments: numAssessmentsActionNeeded,
    renewals: numRenewalsActionNeeded,
  }

  const toMenuItem = (item: RouteNavItem): MenuItemType => {
    const href = hrefFor(item.route)

    return {
      title: t(item.labelKey),
      Icon: item.Icon,
      href,
      selected: (item.activePaths ?? [href]).some((section) => isRouteActive(asPath, section)),
      count: item.count == null ? undefined : (countsByName[item.count] ?? undefined),
      badge: item.badgeKey == null ? undefined : t(item.badgeKey),
    }
  }

  const isVisible = (item: RouteNavItem) => routeAccess.get(item.route) === 'allowed'

  const items = NAV_ITEMS.reduce<MenuItemType[]>((visible, item) => {
    if (!isGroup(item)) {
      return isVisible(item) ? [...visible, toMenuItem(item)] : visible
    }

    const children = item.children.filter(isVisible).map(toMenuItem)
    const [firstChild] = children
    if (firstChild == null || children.length === 1) return [...visible, ...children]

    return [
      ...visible,
      {
        title: t(item.labelKey),
        Icon: item.Icon,
        href: firstChild.href,
        selected: children.some((child) => child.selected),
        children,
      },
    ]
  }, [])

  if (role === Roles.SUPPLIER) {
    return null
  }

  return (
    <SidebarUncontrolled
      isSidebarExpanded={isExpanded}
      onToggleExpanded={toggleExpanded}
      items={items}
      numberOfActiveTasks={taskCount}
      path={path}
    />
  )
}
